import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Pencil, Plus, Inbox, Building2, ExternalLink, Mail, Phone, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, adminApi } from "@/lib/api";
import { COMPANY, SERVICES } from "@/lib/site";

const TOKEN_KEY = "ag_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  if (!token) return <Login onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
  return <Dashboard token={token} onLogout={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }} />;
}

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { password });
      toast.success("Welcome back");
      onLogin(data.token);
    } catch (err) {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1A36] flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md bg-white p-8 sm:p-10 border border-[#D4AF37]/40">
        <Link to="/" className="flex items-center gap-3" data-testid="admin-login-back">
          <img src={COMPANY.logoUrl} alt="" className="h-12 w-12 object-contain" />
          <div>
            <div className="font-display font-extrabold">ASSET GROUP</div>
            <div className="text-[10px] tracking-[0.25em] text-[#D4AF37]">ADMIN PANEL</div>
          </div>
        </Link>
        <h1 className="font-display text-2xl font-extrabold mt-8 text-[#0A1A36]">Sign in</h1>
        <p className="text-sm text-slate-600 mt-1">Use your admin password to manage projects and enquiries.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] font-bold">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-none h-12 border-slate-300 focus-visible:ring-1 focus-visible:ring-[#0A1A36]"
              data-testid="admin-login-password"
              required
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-none h-12 bg-[#D4AF37] hover:bg-[#b8952b] text-[#0A1A36] font-bold uppercase tracking-wider" data-testid="admin-login-submit">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      <header className="bg-[#0A1A36] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-home-link">
            <img src={COMPANY.logoUrl} alt="" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-display font-extrabold tracking-tight">ASSET GROUP · ADMIN</div>
              <div className="text-[10px] tracking-[0.25em] text-[#D4AF37]">CONTROL CENTRE</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-white/80 hover:text-[#D4AF37] hidden sm:flex items-center gap-1" data-testid="admin-view-site">
              View site <ExternalLink className="h-3 w-3" />
            </Link>
            <Button variant="outline" onClick={onLogout} className="rounded-none border-white/30 text-white bg-transparent hover:bg-white hover:text-[#0A1A36]" data-testid="admin-logout">
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Tabs defaultValue="contacts" className="space-y-6">
          <TabsList className="rounded-none bg-white border border-[#E2E8F0] p-1 h-auto">
            <TabsTrigger value="contacts" className="rounded-none data-[state=active]:bg-[#0A1A36] data-[state=active]:text-white px-5 py-2.5 uppercase tracking-wider text-xs font-bold" data-testid="admin-tab-contacts">
              <Inbox className="h-4 w-4 mr-2" /> Enquiries
            </TabsTrigger>
            <TabsTrigger value="projects" className="rounded-none data-[state=active]:bg-[#0A1A36] data-[state=active]:text-white px-5 py-2.5 uppercase tracking-wider text-xs font-bold" data-testid="admin-tab-projects">
              <Building2 className="h-4 w-4 mr-2" /> Projects
            </TabsTrigger>
          </TabsList>

          <TabsContent value="contacts">
            <ContactsPanel token={token} onUnauthorized={onLogout} />
          </TabsContent>
          <TabsContent value="projects">
            <ProjectsPanel token={token} onUnauthorized={onLogout} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function ContactsPanel({ token, onUnauthorized }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi(token).get("/admin/contacts");
      setContacts(data);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this enquiry?")) return;
    try {
      await adminApi(token).delete(`/admin/contacts/${id}`);
      setContacts((arr) => arr.filter((c) => c.id !== id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="bg-white border border-[#E2E8F0]" data-testid="admin-contacts-panel">
      <div className="p-5 border-b border-[#E2E8F0] flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-extrabold text-[#0A1A36]">Contact Enquiries</h2>
          <p className="text-sm text-slate-500">All messages submitted from the website.</p>
        </div>
        <div className="text-sm text-slate-500" data-testid="admin-contacts-count">{contacts.length} total</div>
      </div>
      {loading ? (
        <div className="p-10 text-slate-500">Loading…</div>
      ) : contacts.length === 0 ? (
        <div className="p-12 text-center text-slate-500" data-testid="admin-contacts-empty">No enquiries yet.</div>
      ) : (
        <ul className="divide-y divide-[#E2E8F0]">
          {contacts.map((c) => (
            <li key={c.id} className="p-5 flex flex-col sm:flex-row gap-4 sm:items-start sm:justify-between" data-testid={`admin-contact-row-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-display font-bold text-[#0A1A36]">{c.name}</span>
                  {c.service && <span className="text-[10px] uppercase tracking-[0.18em] font-bold bg-[#0A1A36] text-white px-2 py-0.5">{c.service}</span>}
                  <span className={`text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 border ${c.email_sent ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                    {c.email_sent ? "Email sent" : "Saved (no email)"}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500">
                  <a href={`mailto:${c.email}`} className="flex items-center gap-1 hover:text-[#0A1A36]"><Mail className="h-3 w-3" />{c.email}</a>
                  {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1 hover:text-[#0A1A36]"><Phone className="h-3 w-3" />{c.phone}</a>}
                  <span>{new Date(c.created_at).toLocaleString()}</span>
                </div>
                <p className="mt-3 text-sm text-slate-700 whitespace-pre-wrap">{c.message}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => remove(c.id)} className="rounded-none border-slate-300 text-slate-700 hover:bg-red-50 hover:border-red-300 hover:text-red-600" data-testid={`admin-contact-delete-${c.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const emptyProject = {
  title: "",
  category: "Residential Construction",
  location: "",
  description: "",
  image_url: "",
  status: "completed",
  year: new Date().getFullYear().toString(),
  featured: false,
};

function ProjectsPanel({ token, onUnauthorized }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProject);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyProject); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...emptyProject, ...p }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.image_url.trim()) {
      toast.error("Title and image URL are required");
      return;
    }
    try {
      if (editing) {
        await adminApi(token).put(`/admin/projects/${editing.id}`, form);
        toast.success("Project updated");
      } else {
        await adminApi(token).post("/admin/projects", form);
        toast.success("Project added");
      }
      setOpen(false);
      load();
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized();
      else toast.error("Failed to save project");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this project?")) return;
    try {
      await adminApi(token).delete(`/admin/projects/${id}`);
      setProjects((arr) => arr.filter((p) => p.id !== id));
      toast.success("Project deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="bg-white border border-[#E2E8F0]" data-testid="admin-projects-panel">
      <div className="p-5 border-b border-[#E2E8F0] flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-extrabold text-[#0A1A36]">Projects</h2>
          <p className="text-sm text-slate-500">Add, edit, or remove portfolio entries.</p>
        </div>
        <Button onClick={openNew} className="rounded-none bg-[#0A1A36] hover:bg-[#0F2347] text-white font-bold uppercase tracking-wider" data-testid="admin-add-project">
          <Plus className="h-4 w-4 mr-2" /> Add Project
        </Button>
      </div>
      {loading ? (
        <div className="p-10 text-slate-500">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="p-12 text-center text-slate-500">No projects yet.</div>
      ) : (
        <ul className="divide-y divide-[#E2E8F0]">
          {projects.map((p) => (
            <li key={p.id} className="p-5 flex gap-4 items-start" data-testid={`admin-project-row-${p.id}`}>
              <img src={p.image_url} alt={p.title} className="h-20 w-28 object-cover border border-[#E2E8F0] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold text-[#0A1A36]">{p.title}</span>
                  {p.featured && <Star className="h-4 w-4 text-[#D4AF37] fill-[#D4AF37]" />}
                  <span className="text-[10px] uppercase tracking-[0.18em] font-bold bg-slate-100 text-slate-700 px-2 py-0.5">{p.status}</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">{p.category} · {p.location || "Fiji"} · {p.year}</div>
                {p.description && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{p.description}</p>}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={() => openEdit(p)} className="rounded-none border-slate-300" data-testid={`admin-project-edit-${p.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => remove(p.id)} className="rounded-none border-slate-300 hover:bg-red-50 hover:border-red-300 hover:text-red-600" data-testid={`admin-project-delete-${p.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="admin-project-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#0A1A36]">
              {editing ? "Edit Project" : "Add Project"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <Field label="Title *">
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none h-11 border-slate-300" required data-testid="admin-project-title" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Category">
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-none h-11 border-slate-300" data-testid="admin-project-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-none h-11 border-slate-300" data-testid="admin-project-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="ongoing">Ongoing</SelectItem>
                    <SelectItem value="upcoming">Upcoming</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Location">
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-none h-11 border-slate-300" data-testid="admin-project-location" />
              </Field>
              <Field label="Year">
                <Input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="rounded-none h-11 border-slate-300" data-testid="admin-project-year" />
              </Field>
            </div>
            <Field label="Image URL *">
              <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." className="rounded-none h-11 border-slate-300" required data-testid="admin-project-image" />
            </Field>
            <Field label="Description">
              <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none border-slate-300" data-testid="admin-project-description" />
            </Field>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                className="h-4 w-4 accent-[#D4AF37]"
                data-testid="admin-project-featured"
              />
              Feature on home page
            </label>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
              <Button type="submit" className="rounded-none bg-[#D4AF37] hover:bg-[#b8952b] text-[#0A1A36] font-bold uppercase tracking-wider" data-testid="admin-project-save">
                {editing ? "Save changes" : "Add project"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#0A1A36]">{label}</Label>
    {children}
  </div>
);
